import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import urls from "../../../config/urls.json";
import { useSession } from "../../../hooks/useSession";
import { useStates } from "../../../hooks/useStates";
import { ApiService as api } from "../../../services/ApiService";
import { isCf } from "../../../services/validators";
import { useDynatrace } from "../../../hooks/useDynatrace";
import { useEditAppointment } from "../../../hooks/useEditAppointment";
import moment from "moment";

export const useFilialiDirectPage = () => {
    const navigate = useNavigate();
    const { read, update, batchUpdate, canISkipCfPage, setC5 } = useSession();
    const { states, setCurrentState, isDatiUtenteReady, isCfFetchStart, isCfFetchError, isFilialiSlideBusy } = useStates();
    const { askForEditAppointment, showModal, closeModal } = useEditAppointment();

    const [fetchError, setFetchError] = useState(false);
    const [fetchErrorMessage, setFetchErrorMessage] = useState("");

    let daySlider = useRef();

    useEffect(() => {
        setCurrentState(states.LOAD_FILIALI_SLIDES_END);
        update("codiceFiscale", "");
    }, []);

    const {} = useDynatrace();

    const next = () => {
        if (canISkipCfPage()) {
            setCurrentState(states.FETCH_CF_END);
            if (true == read("ricontattoCf") && true == read("ricontattoLuogo")) {
                update("ricontatto", true);
                navigate(urls.clink.path);
            } else if (true == read("utenteRiconosciuto")) {
                navigate(urls.datiPersonaliRiconosciutoC5.path);
            } else {
                navigate(urls.datiPersonaliNonRiconosciutoC5.path);
            }
        } else {
            navigate(urls.codiceFiscale.path);
        }
    };

    const onGiorniSlideChange = async (giornoIdx) => {
        const isCfAlreadyOk = read("state") >= states.FETCH_CF_END;

        setCurrentState(states.LOAD_FILIALI_SLIDES_START);
        const dataObj = read("disponibilita")[giornoIdx];
        update("dataScelta", dataObj);

        // call new Disponibilita
        let disponibilitaResult = {};
        try {
            disponibilitaResult = await api.GetDayAndTimeSlot({
                IdFiliale: read("filialeScelta").CodiceOcsPV,
                From: moment().toDate(),
                To: moment().add(7, "days").toDate(),
            });
        } catch (error) {
            setFetchErrorMessage("C'è stato un errore nel caricamento delle disponibilità delle filiali. <br />Per favore riprova.");
            setFetchError(true);
            return;
        }

        let orariDisponibili;
        const slots = disponibilitaResult.Slots || [];
        for (let day of slots) {
            const slotDay = moment(day.Date).format("MM-DD");
            const requestDay = moment(dataObj.data).format("MM-DD");
            if (slotDay == requestDay) {
                orariDisponibili = day.Time;
            }
        }
        const orari = [
            { hour: 9, minute: 0, disponibile: false },
            { hour: 9, minute: 30, disponibile: false },
            { hour: 10, minute: 0, disponibile: false },
            { hour: 10, minute: 30, disponibile: false },
            { hour: 11, minute: 0, disponibile: false },
            { hour: 11, minute: 30, disponibile: false },
            { hour: 12, minute: 0, disponibile: false },
            { hour: 12, minute: 30, disponibile: false },
            { hour: 14, minute: 0, disponibile: false },
            { hour: 14, minute: 30, disponibile: false },
            { hour: 15, minute: 0, disponibile: false },
            { hour: 15, minute: 30, disponibile: false },
            { hour: 16, minute: 0, disponibile: false },
            { hour: 16, minute: 30, disponibile: false },
            { hour: 17, minute: 0, disponibile: false },
            { hour: 17, minute: 30, disponibile: false },
        ];
        for (let t in orariDisponibili) {
            const orarioDisponibile = orariDisponibili[t];
            const ora = orarioDisponibile.split(":")[0];
            const minuto = orarioDisponibile.split(":")[1];
            for (let o in orari) {
                if (orari[o].hour == ora && orari[o].minute == minuto) {
                    orari[o].disponibile = true;
                }
            }
        }

        update("orari", orari);

        let favouriteHour = orari.filter((_) => _.disponibile == true)[0];
        const eligibleOrarioScelto = orari.filter((_) => {
            const orarioScelto = read("orarioScelto");
            if (orarioScelto) {
                return _.disponibile == true && _.hour == orarioScelto.hour && _.minute == orarioScelto.minute;
            } else {
                return false;
            }
        });
        if (eligibleOrarioScelto.length > 0) {
            favouriteHour = eligibleOrarioScelto[0];
        }

        update("orarioScelto", favouriteHour);
        setCurrentState(states.LOAD_FILIALI_SLIDES_END);

        if (isCfAlreadyOk) {
            setCurrentState(states.FETCH_CF_END);
        }
    };

    const onCfChange = async (value) => {
        setCurrentState(states.BUSY);
        update("codiceFiscale", value);
        update("codiceFiscale_external", value);

        if (value.length < 16) return;

        if (!isCf(value)) {
            setCurrentState(states.FETCH_CF_ERROR);
            return;
        }

        setCurrentState(states.FETCH_CF_START);

        let cfResult = {};
        try {
            cfResult = await api.GetUserDataAndAppointmentByCf(
                {
                    CodiceFiscale: value,
                },
                3
            );
        } catch (error) {
            setFetchErrorMessage("C'è stato un errore con il codice fiscale inserito. <br />Per favore riprova.");
            setFetchError(true);
            setCurrentState(states.LOAD_FILIALI_SLIDES_END);
            return;
        }

        // let's see if he has an appointment already
        update("oppID", "");

        // WAF...
        if (!cfResult.hasOwnProperty("appointmentData")) {
            setFetchErrorMessage("C'è stato un errore con il codice fiscale inserito. <br />Per favore riprova.");
            setFetchError(true);
            setCurrentState(states.LOAD_FILIALI_SLIDES_END);
            return;
        }

        const utente = cfResult.datiCliente || {};
        update("tipoOccupazione", utente.tipoOccupazione || "");
        const oppID = cfResult.appointmentData && cfResult.appointmentData.appointmentId && cfResult.appointmentData.appointmentId != "" ? cfResult.appointmentData.appointmentId : null;
        if (null != oppID) {
            setCurrentState(states.FETCH_CF_EDIT_APPOINTMENT);
            askForEditAppointment();
            return;
        }

        update("ricontattoCf", cfResult.ricontatto);

        const mergeableFields = {
            utenteRiconosciuto: null != utente.codiceFiscale,
        };
        mergeableFields["nome"] = utente.nome || "";
        mergeableFields["cognome"] = utente.cognome || "";
        mergeableFields["email"] = utente.email || "";
        mergeableFields["cellulare"] = utente.telefono || utente.cellulare || "";
        mergeableFields["capResidenza"] = utente.cap || "";
        // if (cfResult.isAlreadyPP === true) {
        //     mergeableFields["prodotto"] = 1;
        // }
        batchUpdate(mergeableFields);
        setCurrentState(states.FETCH_CF_END);
    };

    const getCfInputState = () => {
        return isCfFetchError() ? 4 : isCf(read("codiceFiscale")) ? 3 : 1;
    };

    return {
        next,
        read,
        update,
        daySlider,
        onGiorniSlideChange,
        isFilialiSlideBusy,
        fetchErrorMessage,
        fetchError,
        onCfChange,
        setFetchError,
        isDatiUtenteReady,
        isCfFetchStart,
        getCfInputState,
        showModal,
        closeModal,
    };
};
